/**
 * GlobeView — the home route. The actual <WildfireGlobe> mounts in AppShell
 * so it stays alive across route changes. This component only renders the
 * globe-specific UI overlays, which read the shared viewer reference from
 * the Zustand store.
 */
import { useGlobeStore } from "@/stores/globe";
import { useHasCesiumToken } from "@/stores/keys";
import { CameraPresetBar } from "./CameraPresets";
import { CoordinateReadout } from "./CoordinateReadout";
import { FeatureInfoPanel } from "./FeatureInfoPanel";
import { GlobeSetupNotice } from "./GlobeSetupNotice";
import { LayerDetailModal } from "./LayerDetailModal";
import { LayerToggleBar, useLayerKeyboardShortcuts } from "./LayerToggleBar";
import { LocationSearch } from "./LocationSearch";

export function GlobeView() {
  const viewer = useGlobeStore((s) => s.viewer);
  useLayerKeyboardShortcuts();
  const hasToken = useHasCesiumToken();

  if (!hasToken) {
    return <GlobeSetupNotice />;
  }

  return (
    <>
      <LocationSearch viewer={viewer} />
      <LayerToggleBar />
      <CameraPresetBar viewer={viewer} />
      <CoordinateReadout viewer={viewer} />
      <FeatureInfoPanel />
      <LayerDetailModal />

      <style>{`
        /* ── The globe overlays on a phone ───────────────────────────────
           These five float around the edges of a wide viewport: search at
           340px top-left, layer toggles top-right, camera presets above the
           coordinate readout bottom-left, and a 380px info panel down the
           right. At 375px they overlap each other and the new bottom tab
           bar, so the rules below re-home them rather than shrink them.

           They live together here, in the component that composes the five,
           because their positions are relative to one another — a reader
           changing one needs the other four in front of them. */
        @media (max-width: 768px) {
          /* Search spans the width instead of claiming 340px of 375. */
          .globe-search {
            left: 12px !important;
            right: 12px !important;
            width: auto !important;
          }

          /* Layer toggles sit under the search field rather than beside it,
             and scroll sideways if the list outgrows one screen. The rows
             keep their own minWidth, so this scrolls rather than squashes. */
          .globe-layers {
            top: 68px !important;
            left: 12px !important;
            right: 12px !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            padding-bottom: 4px !important;
            scrollbar-width: none !important;
          }
          .globe-layers::-webkit-scrollbar { display: none !important; }

          /* Hidden, not moved. Camera presets are a "fly me to a named
             viewpoint" convenience that costs 220px of width, and the
             coordinate readout is a desktop telemetry nicety — both would
             land under the tab bar, and neither is worth a sheet of its
             own. Panning and pinching still reach everywhere they go. */
          .globe-presets,
          .globe-coords {
            display: none !important;
          }

          /* The feature panel becomes a bottom sheet: full width, capped at
             just over half the screen so the map stays visible behind the
             thing the panel is describing. */
          .globe-info {
            top: auto !important;
            left: 8px !important;
            right: 8px !important;
            bottom: 8px !important;
            width: auto !important;
            max-height: 58vh !important;
          }
        }
      `}</style>
    </>
  );
}
